import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Button,
  Input,
  Select,
  Form,
  Row,
  Col,
  Empty,
  Space,
  Tooltip,
  Popconfirm,
  Switch,
  Upload,
  Alert,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, UploadOutlined,
  ApartmentOutlined, DownloadOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { libellesTraduits } from '../../i18n/libelles';
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';

const { Option } = Select;

const TYPES = ['POLICE', 'GENDARMERIE', 'MIXTE'];

const NOM_MODELE = 'modele-entites-constat.xlsx';

const enregistrerFichier = (blob, nom) => {
  const url = window.URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  window.URL.revokeObjectURL(url);
};
// La valeur stockee reste MIXTE : la changer demanderait une migration de l'enum
// backend ET une reprise des lignes existantes. Seul le libelle affiche change.
const TYPE_LABEL = libellesTraduits('parametres:entitesConstat.types');
const TYPE_COLOR = { POLICE: 'blue', GENDARMERIE: 'volcano', MIXTE: 'purple' };

export default function EntitesConstat() {
  const { t } = useTranslation('parametres');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFiltre, setTypeFiltre] = useState();
  const [actifsOnly, setActifsOnly] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  /* L'import passe par une fenetre en deux temps : le modele d'abord, le fichier
     rempli ensuite. Le serveur refuse tout classeur qui ne reprend pas l'en-tete
     du modele - autant que l'ecran le fasse telecharger avant de demander un fichier. */
  const [importOuvert, setImportOuvert] = useState(false);
  const [modeleEnCours, setModeleEnCours] = useState(false);
  const [erreursImport, setErreursImport] = useState([]);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/v1/entites-constat', { params: { actifsOnly } });
      setItems(res.data?.data || []);
    } catch {
      toast.error(t('entitesConstat.messages.erreurChargement'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [actifsOnly]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ actif: true, type: 'POLICE' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      nom: record.nom,
      type: record.type,
      localite: record.localite,
      codePostal: record.codePostal,
      actif: record.actif,
    });
    setModalOpen(true);
  };

  const onSubmit = async (vals) => {
    const payload = {
      nom: vals.nom.trim(),
      type: vals.type,
      localite: vals.localite?.trim() || null,
      codePostal: vals.codePostal?.trim() || null,
      actif: vals.actif !== false,
    };
    try {
      if (editing) {
        await axiosInstance.put(`/v1/entites-constat/${editing.entiteConstatTrackingId}`, payload);
        toast.success(t('entitesConstat.messages.entiteModifiee'));
      } else {
        await axiosInstance.post('/v1/entites-constat', payload);
        toast.success(t('entitesConstat.messages.entiteCreee'));
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('entitesConstat.messages.erreurSauvegarde'));
    }
  };

  const supprimer = async (record) => {
    try {
      await axiosInstance.delete(`/v1/entites-constat/${record.entiteConstatTrackingId}`);
      toast.success(t('entitesConstat.messages.entiteSupprimee'));
      load();
    } catch {
      toast.error(t('entitesConstat.messages.erreurSuppression'));
    }
  };

  const telechargerModele = async () => {
    setModeleEnCours(true);
    try {
      const res = await axiosInstance.get('/v1/entites-constat/import/modele', { responseType: 'blob' });
      enregistrerFichier(res.data, NOM_MODELE);
    } catch {
      toast.error(t('entitesConstat.messages.modeleIndisponible'));
    } finally {
      setModeleEnCours(false);
    }
  };

  const fermerImport = () => {
    setImportOuvert(false);
    setErreursImport([]);
  };

  const importerExcel = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    setErreursImport([]);
    try {
      const res = await axiosInstance.post('/v1/entites-constat/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Les refus sont affichés dans la fenêtre, ligne par ligne : le toast global
        // n'en montrerait qu'un résumé.
        skipErrorToast: true,
      });
      const data = res.data?.data || {};
      toast.success(t('entitesConstat.import.succes', { crees: data.totalCrees || 0, ignores: data.totalIgnores || 0 }));
      fermerImport();
      load();
    } catch (e) {
      // Deux formes de refus : les erreurs de lignes (le fichier suit le modèle mais
      // contient des valeurs invalides), ou un message unique (le fichier ne suit pas
      // le modèle). On montre tout ce que le serveur a dit.
      const erreurs = e?.response?.data?.data?.erreurs || [];
      setErreursImport(erreurs.length
        ? erreurs
        : [e?.response?.data?.message || t('entitesConstat.import.erreur')]);
    } finally {
      setImporting(false);
    }
    return false;
  };

  const filtered = items.filter(i => {
    const matchSearch = !search ||
      i.nom?.toLowerCase().includes(search.toLowerCase()) ||
      i.localite?.toLowerCase().includes(search.toLowerCase());
    const matchType = !typeFiltre || i.type === typeFiltre;
    return matchSearch && matchType;
  });

  const cols = [
    {
      title: t('entitesConstat.colonnes.nom'), dataIndex: 'nom', key: 'nom', ellipsis: true,
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: t('entitesConstat.colonnes.type'), dataIndex: 'type', key: 'type', width: 150,
      render: (v) => <Tag color={TYPE_COLOR[v] || 'default'} style={{ borderRadius: 20 }}>{TYPE_LABEL[v] || v}</Tag>,
    },
    {
      title: t('entitesConstat.colonnes.localite'), dataIndex: 'localite', key: 'loc', width: 200, ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: t('entitesConstat.colonnes.codePostal'), dataIndex: 'codePostal', key: 'cp', width: 120,
      render: (v) => v
        ? <span style={{ fontFamily: 'var(--font-main)', color: '#6b7280' }}>{v}</span>
        : '-',
    },
    {
      title: t('entitesConstat.colonnes.actif'), dataIndex: 'actif', key: 'actif', width: 80, align: 'center',
      render: (v) => v
        ? <Tag color="green" style={{ borderRadius: 20 }}>{t('entitesConstat.colonnes.oui')}</Tag>
        : <Tag color="red" style={{ borderRadius: 20 }}>{t('entitesConstat.colonnes.non')}</Tag>,
    },
    {
      title: '', key: 'actions', width: 90, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={t('entitesConstat.actions.modifier')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} style={{ borderRadius: 6 }} />
          </Tooltip>
          <Popconfirm title={t('entitesConstat.actions.supprimerConfirmation')} onConfirm={() => supprimer(r)} okText={t('entitesConstat.actions.oui')} cancelText={t('entitesConstat.actions.non')}>
            <Tooltip title={t('entitesConstat.actions.supprimer')}>
              <Button size="small" icon={<DeleteOutlined />} danger style={{ borderRadius: 6 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout title={t('entitesConstat.page.titre')}>
      {/* Recherche, type et « actifs seulement » filtrent cote client :
          `filtered.length` est le nombre de lignes reellement affichees. */}
      <PageHeader
        titre={t('entitesConstat.page.titre')}
        icone={<ApartmentOutlined />}
        compte={filtered.length}
        unite={t('entitesConstat.page.unite')}
        unitePluriel={t('entitesConstat.page.unitePluriel')}
        actions={(
          <Space>
            <Tooltip title={t('entitesConstat.actions.actualiser')}>
              <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }} />
            </Tooltip>
            <Button icon={<UploadOutlined />} onClick={() => setImportOuvert(true)} style={{ borderRadius: 8 }}>
              {t('entitesConstat.actions.importerExcel')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
              {t('entitesConstat.actions.ajouter')}
            </Button>
          </Space>
        )}
      />

      <div className="cb-card" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          placeholder={t('entitesConstat.filtres.recherche')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360, flex: 1, minWidth: 240, borderRadius: 8 }}
          allowClear
        />
        <Select
          allowClear
          placeholder={t('entitesConstat.filtres.type')}
          value={typeFiltre}
          onChange={setTypeFiltre}
          style={{ minWidth: 200 }}
        >
          {TYPES.map(t => <Option key={t} value={t}>{TYPE_LABEL[t]}</Option>)}
        </Select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Switch checked={actifsOnly} onChange={setActifsOnly} />
          <span style={{ fontSize: 13, color: '#6b7280' }}>{t('entitesConstat.filtres.actifsUniquement')}</span>
        </div>
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={filtered}
          columns={cols}
          rowKey="entiteConstatTrackingId"
          loading={loading}
          pagination={{ pageSize: 20, showTotal: (total) => t('entitesConstat.pagination.total', { count: total }) }}
          locale={{ emptyText: <Empty description={t('entitesConstat.pagination.aucuneEntite')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>

      <CBModal
        title={editing ? t('entitesConstat.modale.titreModification') : t('entitesConstat.modale.titreCreation')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="nom" label={t('entitesConstat.modale.nom')}
                rules={[{ required: true, message: t('entitesConstat.modale.nomObligatoire') }]}>
                <Input placeholder={t('entitesConstat.modale.nomExemple')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label={t('entitesConstat.modale.type')} rules={[{ required: true }]}>
                <Select>
                  {TYPES.map(t => <Option key={t} value={t}>{TYPE_LABEL[t]}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="codePostal" label={t('entitesConstat.modale.codePostal')}>
                <Input placeholder={t('entitesConstat.modale.codePostalExemple')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="localite" label={t('entitesConstat.modale.localite')}>
                <Input placeholder={t('entitesConstat.modale.localiteExemple')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="actif" label={t('entitesConstat.modale.actif')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setModalOpen(false)}>{t('entitesConstat.modale.annuler')}</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
              {editing ? t('entitesConstat.modale.modifier') : t('entitesConstat.modale.creer')}
            </Button>
          </div>
        </Form>
      </CBModal>
      <CBModal
        title={t('entitesConstat.import.titre')}
        open={importOuvert}
        onCancel={fermerImport}
        footer={null}
        width={560}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="cb-card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
              {t('entitesConstat.import.etape1')}
            </div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
              {t('entitesConstat.import.etape1Aide')}
            </div>
            <Button
              icon={<DownloadOutlined />}
              loading={modeleEnCours}
              onClick={telechargerModele}
              style={{ borderRadius: 8 }}
            >
              {t('entitesConstat.import.telechargerModele')}
            </Button>
          </div>

          <div className="cb-card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
              {t('entitesConstat.import.etape2')}
            </div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
              {t('entitesConstat.import.etape2Aide')}
            </div>
            <Upload
              accept=".xlsx"
              showUploadList={false}
              beforeUpload={importerExcel}
              disabled={importing}
            >
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                loading={importing}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
              >
                {t('entitesConstat.import.choisirFichier')}
              </Button>
            </Upload>
          </div>

          {erreursImport.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={t('entitesConstat.import.refuse')}
              description={(
                <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 180, overflowY: 'auto' }}>
                  {erreursImport.map((err) => <li key={err}>{err}</li>)}
                </ul>
              )}
              style={{ borderRadius: 10 }}
            />
          )}
        </div>
      </CBModal>
    </Layout>
  );
}
